/**
 * useLorData
 *
 * Small hooks for LoR-related data access used by the final claims step.
 * - useLorTemplates: lists available LoR templates for a matter's firm and
 *   suggests a default based on coverage category.
 * - useLorDocuments: lists and sorts LoR documents for a matter.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import {
  LOR_ALLOWED_TEMPLATE_CATEGORIES,
  LOR_TEMPLATE_CATEGORIES,
} from '@/components/claims-filing/constants';
import type { TemplateCategory } from '@/services/api/generated/templatesV2Service';
import { useTemplateListQuery } from '@/services/api/generated/templatesV2Service';
import {
  useGetMatterDocumentsQuery,
  useGetMatterQuery,
} from '@/services/api/mattersService';
import type { DocumentInstanceDto } from '@/services/types/document-types';
import { extractAttorneyId } from '@/utils/attorney-helpers';

import type { MatterDocLite, TemplateLite } from '../../types';

/**
 * useLorTemplates
 *
 * Retrieves and filters document templates for Letters of Representation (LoR)
 * available to the current matter's firm. Provides the filtered list and a
 * helper to pick a sensible default template id based on coverage category.
 *
 * @param matterId The id of the matter whose firm templates to query
 * @returns Immutable object containing:
 * - `lorTemplates`: filtered list of LoR-capable templates
 * - `isLoading`: loading state of the templates query
 * - `error`: request error, if any
 * - `pickDefaultTemplateId`: function that chooses a default template id
 */
export const useLorTemplates = (matterId: string) => {
  const { data: matter } = useGetMatterQuery(matterId);
  const firmId = matter?.firm?.id;
  const {
    data: templatesData,
    isLoading,
    error,
  } = useTemplateListQuery(
    firmId
      ? { firmId: `${firmId},__GLOBAL__`, status: 'ACTIVE' }
      : (skipToken as unknown as { firmId: string; status: 'ACTIVE' }),
  );
  const templates = templatesData?.results ?? [];

  const representingAttorneyId = extractAttorneyId(
    matter?.representing_attorney,
  );
  const representingAttorneyName =
    matter?.representing_attorney_name ??
    (typeof matter?.representing_attorney === 'object' &&
    matter?.representing_attorney !== null &&
    'name' in matter.representing_attorney
      ? ((matter.representing_attorney as { name?: string | null }).name ??
        null)
      : null);

  const lorTemplates = useMemo<TemplateLite[]>(() => {
    const allLor = (templates as TemplateLite[]).filter(
      (t) =>
        LOR_ALLOWED_TEMPLATE_CATEGORIES.has(t.category) &&
        t.format !== 'EXTERNAL',
    );

    // If the firm has at least one firm-scoped template for a given LOR
    // category, exclude global templates in that same category so the
    // dropdown only shows the firm's own templates.
    const firmCategoriesWithTemplates = new Set(
      allLor
        .filter((t) => t.scope === 'FIRM')
        .map((t) => t.category),
    );

    if (firmCategoriesWithTemplates.size === 0) return allLor;

    return allLor.filter(
      (t) =>
        t.scope !== 'GLOBAL' ||
        !firmCategoriesWithTemplates.has(t.category),
    );
  }, [templates]);

  const pickDefaultTemplateId = (
    coverageCategory?: string | null,
    templateList: TemplateLite[] = lorTemplates,
  ) => {
    if (!templateList.length) return null as string | null;

    if (coverageCategory === 'PROPERTY') {
      const preservationTemplate = templateList.find(
        (t) => t.category === 'PRESERVATION_LETTER',
      );
      if (preservationTemplate) {
        return preservationTemplate.id;
      }

      const locationTemplate = templateList.find((t) =>
        (t.name ?? '').toLowerCase().includes('location'),
      );
      if (locationTemplate) {
        return locationTemplate.id;
      }
    }

    let preferredCategory: TemplateCategory | null = null;
    if (coverageCategory === 'FIRST_PARTY') {
      preferredCategory = 'LOR_FIRST_PARTY';
    } else if (coverageCategory === 'THIRD_PARTY') {
      preferredCategory = 'LOR_THIRD_PARTY';
    }
    const preferred = preferredCategory
      ? templateList.find((t) => t.category === preferredCategory)
      : null;
    return (preferred || templateList[0] || null)?.id || null;
  };

  return {
    lorTemplates,
    isLoading,
    error,
    pickDefaultTemplateId,
    representingAttorneyId,
    representingAttorneyName,
  } as const;
};

/**
 * useLorDocuments
 *
 * Fetches matter documents, narrows them to LoR-generated documents, and
 * sorts the result by newest first. Also exposes loading/error state and a
 * `refetch` helper from the underlying query.
 *
 * @param matterId The id of the matter whose documents to fetch
 * @returns Immutable object containing:
 * - `lorDocs`: LoR documents sorted by created_at descending
 * - `isLoading`: loading state for the documents query
 * - `error`: request error, if any
 * - `refetch`: function to refresh the documents list
 */
export const useLorDocuments = (matterId: string) => {
  const {
    data: matterDocs = [],
    isLoading,
    error,
    refetch,
  } = useGetMatterDocumentsQuery(matterId);
  const lorDocs = useMemo<MatterDocLite[]>(() => {
    return (matterDocs as DocumentInstanceDto[])
      .filter((d) => {
        const cat = d.document_template?.category as
          | TemplateCategory
          | undefined;
        return cat ? LOR_TEMPLATE_CATEGORIES.has(cat) : false;
      })
      .map((doc) => {
        // Extract coverage_id from merge_fields or field_values
        const coverageId =
          (doc.merge_fields as Record<string, unknown> | null)?.coverage_id ??
          (doc.field_values as Record<string, unknown> | null)?.coverage_id ??
          null;
        return {
          id: doc.id,
          created_at: doc.created_at,
          document_template: {
            id: doc.document_template?.id ?? null,
            name: doc.document_template?.name,
            category: doc.document_template?.category as
              | TemplateCategory
              | undefined,
          },
          field_values: doc.field_values ?? null,
          merge_fields: doc.merge_fields ?? null,
          coverage_id: typeof coverageId === 'string' ? coverageId : null,
          cached_pdf_url: doc.cached_pdf_url ?? null,
          uploaded_artifact_id: doc.uploaded_artifact_id ?? null,
          original_filename: doc.uploaded_filename ?? null,
          lor_filename: doc.lor_filename ?? null,
        };
      })
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [matterDocs]);
  return { lorDocs, isLoading, error, refetch } as const;
};
